import { BASEURL } from "../BaseUrl";

/**
 * Fetches a list of domains with optional filters and pagination.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The parameters to filter and paginate the domain list.
 * @param {string} data.user_id - The ID of the user requesting the domain list.
 * @param {string} data.search - The search term to filter domains by name or other attributes.
 * @param {string} data.domainSearchParam - The parameter to search by (e.g., name).
 * @param {number} data.pageNumber - The page number for pagination.
 * @param {number} data.pageSize - The number of domains per page for pagination.
 * @param {string} data.status - The status of the domains to filter by.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getAllDomains = async (axios, data) => {
  return await axios.get(`${BASEURL}/domain/get-domain`, {
    params: {
      user_id: data.user_id,
      search: data.search,
      searchParam: data.domainSearchParam,
      pageNumber: data.pageNumber,
      pageSize: data.pageSize,
      status: data.status,
    },
  });
};

/**
 * Deletes a specific domain by ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} domain_id - The ID of the domain to be deleted.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const deleteDomain = async (axios, domain_id) => {
  return await axios.delete(`${BASEURL}/domain/delete-domain`, {
    params: { domain_id },
  });
};

/**
 * Creates a new domain with the provided details.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} user_id - The ID of the user creating the domain.
 * @param {string} username - The username of the user creating the domain.
 * @param {string} domainName - The name of the new domain.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const createDomain = async (axios, user_id, username, domainName) => {
  return await axios.post(`${BASEURL}/domain/add-domain`, {
    userId: user_id,
    domainName: domainName,
    userName: username,
  });
};

/**
 * Updates the status of a specific domain.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data containing the domain ID and new status.
 * @param {string} data.domain_id - The ID of the domain whose status is to be updated.
 * @param {string} data.status - The new status to set for the domain.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const domainStatusChanged = async (axios, data) => {
  return await axios.put(`${BASEURL}/domain/domain-status-changed`, null, {
    params: {
      domain_id: data.domain_id,
      status: data.status,
    },
  });
};

export const DomainApi = {
  getAllDomains,
  deleteDomain,
  createDomain,
  domainStatusChanged,
};
